import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';

const Card = styled.div`
  background: ${(props) =>
    props.theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.9)'};
  border: 1px solid
    ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background: ${(props) =>
      props.theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 1)'};
    border-color: rgba(250, 70, 22, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background: ${(props) => props.gradient};
  overflow: hidden;
`;

const ThumbnailImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 0%,
    transparent 50%,
    transparent 100%
  );
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(1);
  width: 64px;
  height: 48px;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  ${Card}:hover & {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.05);
    background: #fa4616;
  }

  svg {
    margin-left: 3px; /* Optical centering for play triangle */
  }
`;

const Duration = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 4px 8px;
  background: #fa4616;
  border-radius: 4px;
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const BadgesContainer = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 6px;
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  background: #fa4616;
  color: white;
`;

const CardContent = styled.div`
  padding: 16px;
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => (props.theme?.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s;

  ${Card}:hover & {
    color: #fa4616;
  }
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${(props) => (props.theme?.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  color: #fa4616;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #e03e0c;
  }
`;

const PlayIcon = () => (
  <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const FireIcon = () => (
  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M12.963 2.286a.75.75 0 00-1.071-.136 9.742 9.742 0 00-3.539 6.177A7.547 7.547 0 016.648 6.61a.75.75 0 00-1.152.082A9 9 0 1015.68 4.534a7.46 7.46 0 01-2.717-2.248z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowTopRightIcon = () => (
  <svg
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

const gradients = [
  'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
  'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
  'linear-gradient(135deg, #ec4899 0%, #f43f5e 100%)',
  'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
];

const VideoCard = ({ video, onClick, showLearnMore = false, helpBaseUrl }) => {
  const [imgError, setImgError] = useState(false);
  const gradientIndex = parseInt(video.vimeoId) % gradients.length;

  return (
    <Card onClick={() => onClick(video)}>
      <ThumbnailContainer gradient={gradients[gradientIndex]}>
        {!imgError && (
          <ThumbnailImage
            src={`https://vumbnail.com/${video.vimeoId}.jpg`}
            alt={video.title}
            onError={() => setImgError(true)}
          />
        )}
        <ThumbnailOverlay />
        <PlayButton>
          <PlayIcon />
        </PlayButton>
        <Duration>{video.duration}</Duration>
        <BadgesContainer>
          {video.isNew && <Badge variant="new">NEW</Badge>}
          {video.isFeatured && (
            <Badge variant="featured">
              <FireIcon /> Featured
            </Badge>
          )}
        </BadgesContainer>
      </ThumbnailContainer>
      <CardContent>
        <CardTitle>{video.title}</CardTitle>
        <CardDescription>{video.description}</CardDescription>
        {showLearnMore && (video.helpLink || helpBaseUrl) && (
          <LearnMoreLink
            href={video.helpLink || helpBaseUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Learn More</span>
            <ArrowTopRightIcon />
          </LearnMoreLink>
        )}
      </CardContent>
    </Card>
  );
};

VideoCard.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    duration: PropTypes.string.isRequired,
    vimeoId: PropTypes.string.isRequired,
    isNew: PropTypes.bool,
    isFeatured: PropTypes.bool,
    helpLink: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  showLearnMore: PropTypes.bool,
  helpBaseUrl: PropTypes.string,
};

export default VideoCard;
